import { createSlice } from '@reduxjs/toolkit';
import * as api from 'api/trials';

export const initialState = {
    list: [],
    total: 0,
    error: [],
    isLoading: false,
    searchQuery: '',
    showDisabled: false,
    metadata: {
        total: 0,
        noActiveTrials: 0,
        noInactiveTrials: 0
    },
    pagination: {
        pageSize: 10,
        pageNumber: 1
    },
    selected: {},
    sorting: {
        field: 'createdAt',
        value: -1 // 1 = asc; -1 = desc
    },
    newGroup: {}
};

const slice = createSlice({
    name: 'trials',
    initialState,
    reducers: {
        setList(state, action) {
            state.list = action.payload.data;
            state.total = action.payload.total;
            state.isLoading = false;
            state.metadata = action.payload.metadata;
        },
        setIsLoading(state, action) {
            state.isLoading = action.payload;
        },
        setCurrentPage(state, action) {
            state.pagination.pageNumber = action.payload;
        },
        setRowsPerPage(state, action) {
            state.pagination.pageSize = action.payload;
        },
        setSearchQuery(state, action) {
            state.pagination.pageNumber = 1; // reset to page 1
            state.searchQuery = action.payload;
        },
        selectedDeploymentGroup(state, action) {
            state.selected = action.payload;
        },
        setNewGroup(state, action) {
            state.newGroup = action.payload;
        },
        setSorting(state, action) {
            state.pagination.pageNumber = 1;
            state.sorting = action.payload;
        },
        setShowDisabled(state, action) {
            state.pagination.pageNumber = 1; // reset to page 1
            state.showDisabled = action.payload;
        }
    }
});

export default slice.reducer;

export const fetchTrials = (params) => async (dispatch, getState) => {
    dispatch(slice.actions.setIsLoading(true));

    try {
        const { pagination, showDisabled, sorting } = getState().trials;

        // Merge provided params with current pagination
        const query = {
            pagination: params?.pagination || pagination,
            search: params?.search ?? '',
            showDisabled: params?.showDisabled ?? showDisabled,
            sorting: params?.sorting || sorting
        };

        // set isLoading to true
        dispatch(slice.actions.setIsLoading(true));

        // API call to fetch trials
        const response = await api.fetchTrials({
            ...query.pagination,
            search: query.search,
            showDisabled: query.showDisabled,
            sortField: query.sorting.field,
            sortOrder: query.sorting.value
        });

        const { metadata, data } = response;

        // Recalculate metadata based on filtered data
        const recalculatedMetadata = {
            total: query.showDisabled ? metadata.total : metadata?.noActiveTrials,
            noActiveTrials: metadata?.noActiveTrials || 0,
            noInactiveTrials: metadata?.noInactiveTrials || 0
        };

        dispatch(
            slice.actions.setList({
                ...response,
                data: data || [],
                total: metadata?.total || 0,
                metadata: recalculatedMetadata
            })
        );
    } catch (error) {
        // Handle error, optionally dispatch error action or notification
        dispatch(slice.actions.setIsLoading(false));
    }
};

export const softDeleteTrial = (trialId) => async (dispatch) => {
    dispatch(slice.actions.setIsLoading(true));
    try {
        // API call to soft delete trial
        await api.softDeleteTrial(trialId);
        // Optionally dispatch success action or notification
    } finally {
        dispatch(slice.actions.setIsLoading(false));
    }
};

export const sendEmail = (clientId, emailData) => async (dispatch) => {
    try {
        // API call to send email
        await api.sendTrialEmail({ id: clientId, ...emailData });
        // Optionally dispatch success action or notification
    } finally {
        dispatch(slice.actions.setIsLoading(false));
    }
};

export const trialCurrentPage = (val) => async (dispatch) => {
    await dispatch(slice.actions.setCurrentPage(val));
    await dispatch(fetchTrials());
};

export const trialRowsPerPage = (val) => async (dispatch) => {
    dispatch(slice.actions.setRowsPerPage(val));
};

// TODO clients list toggle for disabled clients
export const showDisabledTrials = (val) => async (dispatch) => {
    await dispatch(slice.actions.setShowDisabled(val));
    await dispatch(fetchTrials({ showDisabled: val }));
};

export const setTrialSorting = (val) => async (dispatch) => {
    await dispatch(slice.actions.setSorting(val));
    await dispatch(fetchTrials());
};

export function updateTrial(trialId, trialData) {
    return async (dispatch) => {
        try {
            const response = await api.updateTrial(trialId, trialData);
            await dispatch(fetchTrials());
            return response;
        } catch (err) {
            dispatch(slice.actions.setIsLoading(false));
            throw err;
        }
    };
}

export function extendTrial(params) {
    return async (dispatch) => {
        try {
            const response = await api.extendTrial(params);
            await dispatch(fetchTrials());
            return response;
        } catch (err) {
            dispatch(slice.actions.setIsLoading(false));
            throw err;
        }
    };
}

export const {
    setList,
    setIsLoading,
    setCurrentPage,
    setRowsPerPage,
    setSearchQuery,
    selectedDeploymentGroup,
    setNewGroup,
    setShowDisabled,
    setSorting
} = slice.actions;
slice.actions;
